<template>
  <div :class="['footer-container', { collapsed: collapsed} ]">
    <div class="input-container"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      :class="{ 'drag-over': isDragOver }">

      <!-- File preview bar -->
      <div v-if="selectedFile" class="file-preview-bar">
        <div class="file-preview-content">
          <img v-if="filePreview" :src="filePreview" class="file-preview-thumb" alt="Preview" />
          <span v-else class="file-preview-icon">&#128196;</span>
          <span class="file-preview-name">{{ selectedFile.name }}</span>
        </div>
        <button class="file-preview-remove" @click="removeFile">&times;</button>
      </div>

      <div class="input-content">
        <!-- Upload icon (only for assistants that support file upload) -->
        <div v-if="supportsUpload" class="icon-container">
          <div class="icon upload-icon" @click="triggerFileInput">
            <img
              :src="require('@/assets/icons/plus-icon.svg')"
              alt="Upload file" />
          </div>
        </div>

        <!-- Hidden file input -->
        <input
          ref="fileInput"
          type="file"
          :accept="acceptedFileTypes"
          style="display: none"
          @change="onFileSelected"
        />

        <!-- Thinking dots while waiting for API -->
        <div v-if="isThinking" class="thinking-indicator">
          <span class="dot"></span><span class="dot"></span><span class="dot"></span>
        </div>

        <!-- Multiline textbox -->
        <div
          ref="editor"
          :contenteditable="!isDisabled"
          class="input-text"
          @input="onInput"
          @keydown.enter.exact.prevent="sendMessage"
          @paste="onPaste"
          :data-placeholder="isDisabled ? '' : 'Ask ' + companyInformation.product_name"
        ></div>

        <!-- Send icon -->
        <div class="send-icon"
          :class="{ 'send-icon-enabled': canSend }"
          @click="sendMessage">
          <img
            :src="require('@/assets/icons/send-icon.svg')"
            alt="Send Icon" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
const DOCUMENT_EXTENSIONS = ['.pdf', '.docx', '.txt'];
const ALL_EXTENSIONS = [...IMAGE_EXTENSIONS, ...DOCUMENT_EXTENSIONS];
const IMAGE_MAX_SIZE = 10 * 1024 * 1024;
const DOC_MAX_SIZE = 20 * 1024 * 1024;

export default {
  name: 'ChatFooter',
  props: {
    isThinking: Boolean,
    collapsed: Boolean,
    companyInformation: Object,
    selectedChatbot: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      message: '',
      isDisabled: false,
      selectedFile: null,
      filePreview: null,
      isDragOver: false,
    };
  },
  computed: {
    supportsUpload() {
      return this.selectedChatbot && this.selectedChatbot.supports_file_upload;
    },
    acceptedFileTypes() {
      return ALL_EXTENSIONS.join(',');
    },
    canSend() {
      return (this.message.trim() !== '' || this.selectedFile) && !this.isDisabled;
    }
  },
  methods: {
    onInput() {
      this.message = this.$refs.editor.innerText;

      if (this.message.trim() === '') {
        this.$refs.editor.innerHTML = '';
      }
      this.resizeInput();
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    onFileSelected(event) {
      const file = event.target.files[0];
      if (file) {
        this.attachFile(file);
      }
      // Reset so the same file can be re-selected
      event.target.value = '';
    },

    attachFile(file) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!ALL_EXTENSIONS.includes(ext)) {
        alert(`Unsupported file type: ${ext}. Allowed: ${ALL_EXTENSIONS.join(', ')}`);
        return;
      }
      const isImage = IMAGE_EXTENSIONS.includes(ext);
      const maxSize = isImage ? IMAGE_MAX_SIZE : DOC_MAX_SIZE;
      if (file.size > maxSize) {
        const maxMB = maxSize / (1024 * 1024);
        alert(`File too large. Maximum size: ${maxMB}MB`);
        return;
      }

      this.selectedFile = file;

      if (isImage) {
        const reader = new FileReader();
        reader.onload = (e) => { this.filePreview = e.target.result; };
        reader.readAsDataURL(file);
      } else {
        this.filePreview = null;
      }
    },

    removeFile() {
      this.selectedFile = null;
      this.filePreview = null;
    },

    onDragOver() {
      if (this.supportsUpload) {
        this.isDragOver = true;
      }
    },
    onDragLeave() {
      this.isDragOver = false;
    },
    onDrop(event) {
      this.isDragOver = false;
      if (!this.supportsUpload) return;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.attachFile(file);
      }
    },

    onPaste(event) {
      if (!this.supportsUpload) return;
      const items = event.clipboardData && event.clipboardData.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file) {
            // Give pasted images a name
            const ext = file.type.split('/')[1] || 'png';
            const named = new File([file], `pasted-image.${ext}`, { type: file.type });
            this.attachFile(named);
          }
          return;
        }
      }
    },

    sendMessage() {
      if (!this.canSend) return;

      this.isDisabled = true;
      const msg = this.message.trim();
      const file = this.selectedFile;

      if (file) {
        this.$emit('send-message', { message: msg, file: file, preview: this.filePreview });
      } else {
        this.$emit('send-message', msg);
      }

      this.message = '';
      this.selectedFile = null;
      this.filePreview = null;
      this.$nextTick(() => {
        this.$refs.editor.innerHTML = '';
        this.resizeInput();
      });
    },

    enableInput() {
      this.isDisabled = false;
    },
    resizeInput() {
      const editor = this.$refs.editor;
      if (!editor) return;
      editor.style.height = 'auto';

      if (!this.message.trim()) {
        editor.style.height = '3.1rem';
      } else {
        editor.style.height = editor.scrollHeight + 'px';
      }
    },
    loadConversation(conversation) {
      this.$emit('load-conversation', conversation);
    }
  }
};
</script>

<style scoped>
.footer-container {
  z-index: 2;
  width: calc(100% - 20.00rem);
  display: flex;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  position: fixed;
  bottom: 0;
  padding: 1rem;
  padding-top: .95rem;
  box-sizing: border-box;
}

.footer-container.collapsed {
  width: calc(100% - 5.80rem);
  box-sizing: content-box;
}

.input-container {
  width: 100%;
  max-width: 800px;
  background: #F7F9FB;
  border-radius: 1rem;
  padding: 0.25rem;
  box-sizing: border-box;
  border: 1px solid #ccc;
  transition: border-color 0.2s;
}

.input-container.drag-over {
  border-color: #4a90d9;
  background: #eef4fb;
}

.input-content {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  width: 100%;
}

.icon-container {
  display: flex;
  gap: 1rem;
}

.icon {
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.upload-icon {
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
  margin-left: 8px;
}

.upload-icon:hover {
  opacity: 1;
}

.file-preview-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: #eef1f4;
  border-radius: 8px 8px 0 0;
  border-bottom: 1px solid #ddd;
}

.file-preview-content {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
}

.file-preview-thumb {
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: 4px;
}

.file-preview-icon {
  font-size: 24px;
}

.file-preview-name {
  font-size: 13px;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

.file-preview-remove {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #999;
  padding: 0 4px;
}

.file-preview-remove:hover {
  color: #333;
}

.input-text {
  flex: 1;
  min-height: 2.5rem;
  max-height: 450px;
  font-size: 1rem;
  font-family: Inter, sans-serif;
  line-height: 1.5rem;
  padding: 0.8rem 0.8rem;
  resize: none;
  border: none;
  outline: none;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  box-sizing: border-box;
}

.input-text[contenteditable="false"] {
  cursor: pointer !important;
  caret-color: transparent !important;
  user-select: none;
}

[contenteditable]:empty::before {
  content: attr(data-placeholder);
  color: #aaa;
  pointer-events: none;
}

.input-text::-webkit-scrollbar {
  width: 8px;
}

.input-text::-webkit-scrollbar-track {
  background: transparent;
}

.input-text::-webkit-scrollbar-thumb {
  background-color: #555;
  border-radius: 10px;
}

.input-text::-webkit-scrollbar-thumb:hover {
  background-color: #888;
}

.send-icon {
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 5px;
  align-self: flex-end;
  margin-bottom: 7px;
}

.send-icon-enabled {
  background: #d6d9db;
  border-radius: 50%;
}

.thinking-indicator {
  display: flex;
  gap: 5px;
  margin: 10px 0;
  padding: 10px 0;
  margin-left: 15px;
}

.dot {
  width: 8px;
  height: 8px;
  background: gray;
  border-radius: 50%;
  animation: blink 1.4s infinite both;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes blink {

  0%,
  80%,
  100% {
    opacity: 0;
  }

  40% {
    opacity: 1;
  }
}
</style>
